import { onBoardPawns } from './board';
import { validateLink } from './apply';
import {
  checkEpitaphReplay,
  currentChainScore,
  hasPlayableLink,
  makeRouteStep,
  resolveLink,
  type ReplayCheck,
} from './rules';
import { hasRule, maxLinks } from './ledger/state';
import { cloneState } from './state';
import type {
  EngineError,
  Epitaph,
  Face,
  Inscription,
  MatchEvent,
  MatchState,
  Pawn,
  PlayLinkAction,
  RouteStep,
  ScoreBreakdown,
  Side,
  Square,
} from './types';

/** What a link would do if committed now (§19.6, §19.7). Uses only public and own information. */
export interface LinkPreview {
  readonly step: RouteStep;
  readonly linkIndex: number;
  /** The chain's score if it ended right after this link. */
  readonly breakdown: ScoreBreakdown;
  /** Captured pawns and the Epitaph route each would hand to its owner. */
  readonly captures: readonly {
    readonly pawnId: string;
    readonly square: Square;
    readonly epitaph: Inscription | null;
  }[];
  readonly crowned: boolean;
  /** Variant B: the crown bonus would be pending rather than scored. */
  readonly crownBonusPending: boolean;
  /** The top the next player would face. */
  readonly newTop: { readonly face: Face; readonly wasWild: boolean };
  /** True when the chain would end right after this link (link limit or no playable link). */
  readonly chainEnds: boolean;
  readonly collateralPawnId: string | null;
}

export function previewLink(
  s: MatchState,
  action: PlayLinkAction,
): LinkPreview | { error: EngineError } {
  if (s.phase !== 'ACTION' && s.phase !== 'CHAIN')
    return { error: { code: 'WRONG_PHASE', message: 'no link can be played now' } };
  if (action.side !== s.decider)
    return { error: { code: 'NOT_YOUR_DECISION', message: `${s.decider} must act now` } };
  const lo = validateLink(s, action);
  if ('error' in lo) return lo;
  const side: Side = action.side;
  const scratch = cloneState(s);
  const events: MatchEvent[] = [];
  const pawn = s.pawns[lo.pawnId] as Pawn;
  const linkIndex = s.chain ? s.chain.links.length : 0;
  resolveLink(scratch, side, lo, events);
  const breakdown = currentChainScore(scratch) as ScoreBreakdown;
  const crowned = events.some((e) => e.type === 'PawnCrowned');
  const chainEnds =
    (scratch.chain?.links.length ?? 0) >= maxLinks(s, side) || !hasPlayableLink(scratch, side);
  // Long Night (Veiled): the Widow's inscriptions stay hidden from South.
  const veiled = !!s.ledger && side === 'S' && hasRule(s, 'veiled', 'S');
  return {
    step: makeRouteStep(lo.face, lo.wasWild, pawn.square as Square, lo.option, crowned),
    linkIndex,
    breakdown,
    captures: lo.option.captures.map((id) => {
      const p = s.pawns[id] as Pawn;
      const hidden = veiled && p.side === 'N';
      return { pawnId: id, square: p.square as Square, epitaph: hidden ? null : p.inscription };
    }),
    crowned,
    crownBonusPending:
      crowned && s.config.crownMode === 'delayed' && !s.players[side === 'S' ? 'N' : 'S'].passed,
    newTop: { face: lo.face, wasWild: lo.wasWild },
    chainEnds,
    collateralPawnId: lo.collateralPawnId,
  };
}

export interface EpitaphOption {
  readonly epitaph: Epitaph;
  readonly pawnId: string;
  readonly check: ReplayCheck;
}

/** Every (Epitaph, pawn) pair for the side, with the replay result or the reason it fails (§19.8). */
export function epitaphOptions(s: MatchState, side: Side): EpitaphOption[] {
  const out: EpitaphOption[] = [];
  for (const epitaph of s.players[side].epitaphs)
    for (const pawn of onBoardPawns(s.pawns, side))
      out.push({ epitaph, pawnId: pawn.id, check: checkEpitaphReplay(s, side, epitaph, pawn.id) });
  return out;
}
